import { useState, lazy, Suspense } from 'react';
import '@/App.css';
import '@/index.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { Toaster } from 'sonner';
import TopNav from '@/components/layout/TopNav';
import QuickExitDock from '@/components/safety/QuickExitDock';
import AuthModal from '@/components/auth/AuthModal';
import Landing from '@/pages/Landing';
import AuthCallback from '@/pages/AuthCallback';

// Route-level code splitting: keep the landing (LCP page) eager, lazy-load the
// rest so the first paint ships the smallest possible bundle.
const PricingPage = lazy(() => import('@/pages/PricingPage'));
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'));
const Onboarding = lazy(() => import('@/pages/Onboarding'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const HealthPage = lazy(() => import('@/pages/HealthPage'));
const DejaPage = lazy(() => import('@/pages/DejaPage'));
const WorldWindow = lazy(() => import('@/pages/WorldWindow'));
const SafetyPage = lazy(() => import('@/pages/SafetyPage'));
const AnchorPublic = lazy(() => import('@/pages/AnchorPublic'));
const GuardianAccept = lazy(() => import('@/pages/GuardianAccept'));
const CirclePage = lazy(() => import('@/pages/CirclePage'));
const Resources = lazy(() => import('@/pages/Resources'));
const ResourceArticle = lazy(() => import('@/pages/ResourceArticle'));
const Enterprise = lazy(() => import('@/pages/Enterprise'));
const PrivacySecurityPage = lazy(() => import('@/pages/PrivacySecurityPage'));
const FamilyPortal = lazy(() => import('@/pages/FamilyPortal'));
const MyLife = lazy(() => import('@/pages/MyLife'));
const LifeStory = lazy(() => import('@/pages/LifeStory'));
const Community = lazy(() => import('@/pages/Community'));
const ResidentSend = lazy(() => import('@/pages/ResidentSend'));
const ResidentView = lazy(() => import('@/pages/ResidentView'));
const Academy = lazy(() => import('@/pages/Academy'));
const AcademyCourse = lazy(() => import('@/pages/AcademyCourse'));
const CertificateVerify = lazy(() => import('@/pages/CertificateVerify'));
const LightRoom = lazy(() => import('@/pages/LightRoom'));

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 rounded-full bg-mimosa/30 orb-breath" /></div>
);

function Protected({ children, needsSanctuary = true }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 rounded-full bg-mimosa/30 orb-breath" /></div>;
  if (!user) return <Navigate to="/" replace />;
  if (needsSanctuary && !user.sanctuary_slug) return <Navigate to="/onboarding" replace />;
  return children;
}

function Router() {
  const location = useLocation();
  const { user } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);

  // Handle OAuth callback synchronously (fragment includes session_id)
  if (location.hash?.includes('session_id=')) return <AuthCallback />;

  const isPublicAnchor = (location.pathname.startsWith('/with/')
    || location.pathname.startsWith('/circle/')
    || location.pathname.startsWith('/family/')
    || location.pathname.startsWith('/life/')
    || location.pathname.startsWith('/for/')
    || location.pathname.startsWith('/window/')
    || location.pathname.startsWith('/guardian/')) && !user;
  const showNav = !isPublicAnchor;

  return (
    <>
      {showNav && <TopNav onSignIn={() => setAuthOpen(true)} />}
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
      <main id="main-content">
        <Suspense fallback={<RouteFallback />}>
        <Routes>
        <Route path="/" element={<Landing onSignIn={() => setAuthOpen(true)} />} />
        <Route path="/safety" element={<SafetyPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/privacy-and-security" element={<PrivacySecurityPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/resources/:slug" element={<ResourceArticle />} />
        <Route path="/vs/wysa" element={<ResourceArticle vsPath="/vs/wysa" />} />
        <Route path="/vs/international-sos" element={<ResourceArticle vsPath="/vs/international-sos" />} />
        <Route path="/enterprise" element={<Enterprise />} />
        <Route path="/duty-of-care" element={<Navigate to="/enterprise" replace />} />
        <Route path="/with/ayesha-uy" element={<Navigate to="/with/ayeshauy" replace />} />
        <Route path="/with/:slug" element={<AnchorPublic />} />
        <Route path="/with/:slug/:token" element={<AnchorPublic />} />
        <Route path="/guardian/accept/:token" element={<GuardianAccept />} />
        <Route path="/circle/:token" element={<CirclePage />} />
        <Route path="/family/:token" element={<FamilyPortal />} />
        <Route path="/life/:token" element={<LifeStory />} />
        <Route path="/for/:token" element={<ResidentSend />} />
        <Route path="/window/:token" element={<ResidentView />} />
        <Route path="/academy/verify/:id" element={<CertificateVerify />} />
        <Route path="/onboarding" element={
          <ProtectedNoSanctuary><Onboarding /></ProtectedNoSanctuary>
        } />
        <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
        <Route path="/dashboard/health" element={<Protected><HealthPage /></Protected>} />
        <Route path="/dashboard/deja" element={<Protected><DejaPage /></Protected>} />
        <Route path="/dashboard/world" element={<Protected><WorldWindow /></Protected>} />
        <Route path="/dashboard/life" element={<Protected><MyLife /></Protected>} />
        <Route path="/dashboard/academy" element={<Protected><Academy /></Protected>} />
        <Route path="/dashboard/academy/:slug" element={<Protected><AcademyCourse /></Protected>} />
        <Route path="/dashboard/light" element={<Protected><LightRoom /></Protected>} />
        <Route path="/community" element={<Protected><Community /></Protected>} />
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </main>
      <QuickExitDock />
    </>
  );
}

function ProtectedNoSanctuary({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 rounded-full bg-mimosa/30 orb-breath" /></div>;
  if (!user) return <Navigate to="/" replace />;
  if (user.sanctuary_slug) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Router />
        <Toaster position="top-center" toastOptions={{ style: { background: '#FBFBF9', color: '#1F2623', border: '1px solid rgba(76,93,75,0.15)', fontFamily: 'DM Sans' } }} />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
